import React, { useState } from 'react';
import { useOrders } from '../hooks/useOrders';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Pagination from './Pagination';
import OrderDetailsModal from './OrderDetailsModal';
import axiosInstance from '../utils/axiosInstance';

const OrdersData = () => {
    const { t } = useTranslation();
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [orderDetails, setOrderDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    
    const { 
        orders, 
        pagination, 
        loading, 
        handleNextPage, 
        handlePreviousPage, 
        handlePageChange 
    } = useOrders('/api/orders/my', 1);

    // Function to fetch order details
    const fetchOrderDetails = async (orderId) => {
        setLoadingDetails(true);
        try {
            const response = await axiosInstance.get(`/api/orders/${orderId}`);
            setOrderDetails(response.data.data);
        } catch (error) {
            console.error('Error fetching order details:', error);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Handle clicking the View button
    const handleViewDetails = (e, order) => {
        e.preventDefault();
        setSelectedOrder(order);
        setOrderDetails(null); // Reset previous details
        setIsModalOpen(true);
        fetchOrderDetails(order.id);
    };

    // Close the modal
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedOrder(null);
        setOrderDetails(null);
    };

    // Render empty state if no orders
    if (!loading && (!orders || orders.length === 0)) {
        return (
            <div className="py-40 text-center">
                <div className="mb-24">
                    <i className="text-gray-500 ph-bold ph-shopping-cart-simple" style={{ fontSize: '64px' }}></i>
                </div>
                <h3 className="mb-16">{t('orders.empty_title') || 'No Orders Found'}</h3>
                <p className="mb-32 text-gray-500">{t('orders.empty_message') || 'You have not made any orders yet.'}</p>
                <Link to="/shop" className="px-32 btn btn-main py-18 rounded-8">
                    {t('checkout.continue_shopping')}
                </Link>
            </div>
        );
    }

    // Map status to badge class
    const getStatusBadgeClass = (status) => {
        if (!status) return 'bg-gray-100 text-gray-700';
        
        // Handle status as an object with properties
        if (typeof status === 'object') {
            if (status.cancelled) {
                return 'bg-danger-100 text-danger-700';
            } else if (status.final) {
                return 'bg-success-100 text-success-700';
            } else if (status.paid) {
                return 'bg-info-100 text-info-700';
            } else if (status.initial) {
                return 'bg-warning-100 text-warning-700';
            } else {
                return 'bg-gray-100 text-gray-700';
            }
        } 
        
        // Handle status as a string (fallback)
        else if (typeof status === 'string') {
            switch (status.toLowerCase()) {
                case 'completed':
                    return 'bg-success-100 text-success-700';
                case 'pending':
                    return 'bg-warning-100 text-warning-700';
                case 'processing':
                    return 'bg-info-100 text-info-700';
                case 'cancelled':
                    return 'bg-danger-100 text-danger-700';
                default:
                    return 'bg-gray-100 text-gray-700';
            }
        }
        
        return 'bg-gray-100 text-gray-700';
    };

    return (
        <div className="orders-data">
            <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">
                {loading ? (
                    <div className="py-40 text-center">
                        <div className="spinner-border text-main-600" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <table className="table style-three">
                            <thead>
                                <tr>
                                    <th className="mb-0 text-lg h6 fw-bold">{t('orders.order_id') || 'Order ID'}</th>
                                    <th className="mb-0 text-lg h6 fw-bold">{t('orders.date') || 'Date'}</th>
                                    <th className="mb-0 text-lg h6 fw-bold">{t('orders.status') || 'Status'}</th>
                                    <th className="mb-0 text-lg h6 fw-bold">{t('orders.total') || 'Total'}</th>
                                    <th className="mb-0 text-lg h6 fw-bold">{t('orders.payment_method') || 'Payment Method'}</th>
                                    <th className="mb-0 text-lg h6 fw-bold">{t('orders.actions') || 'Actions'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((order) => (
                                    <tr key={order.id}>
                                        <td>
                                            <span className="fw-medium">#{order.reference || order.id}</span>
                                        </td>
                                        <td>
                                            {order.createdAt}
                                        </td>
                                        <td>
                                            <span className={`px-16 py-8 rounded-pill ${getStatusBadgeClass(order.status)}`}>
                                                {order.status?.name || order.status || 'Unknown'}
                                            </span>
                                        </td>
                                        <td>
                                            {order.totalFormatted}
                                        </td>
                                        <td>
                                            {order.paymentMethod || '-'}
                                        </td>
                                        <td>
                                            <button 
                                                className="px-16 py-8 btn btn-sm btn-outline-main rounded-6"
                                                onClick={(e) => handleViewDetails(e, order)}
                                            >
                                                <i className="ph ph-eye me-8"></i>
                                                {t('orders.view_details') || 'View'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        {pagination.total > pagination.per_page && (
                            <div className="mt-40 d-flex justify-content-center">
                                <Pagination
                                    pagination={pagination}
                                    onNextPage={handleNextPage}
                                    onPreviousPage={handlePreviousPage}
                                    onPage={handlePageChange}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Order Details Modal */}
            <OrderDetailsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                orderDetails={orderDetails}
                loading={loadingDetails}
            />
        </div>
    );
};

export default OrdersData; 